import { z } from 'zod';
import { BATTLE_RES_IDS, CRATE_SIZES, CRATE_TYPES, SHIP_AVAILABILITY, SHIP_OPS, SHIP_ROLES, SUP_CATS, type SupCat } from './constants.ts';
import { MAX_AMOUNT } from './quantity.ts';

// Supply's shapes, used by the server and the browser (design §7.3, plan 2B
// Assumptions 1–5). Amounts are integers in an item's base unit: crates, kits or
// units counted, or thousandths of an SCU (quantity.ts).

export const RecordId = z.string().regex(/^[a-z0-9]{15}$/, 'must be a record id');

/** Display names by user id, for the people a response names. */
export const People = z.record(z.string(), z.string());
export type People = z.infer<typeof People>;

export const Person = z.object({ id: z.string(), displayName: z.string() });
export type Person = z.infer<typeof Person>;

export const Category = z.enum(SUP_CATS);
export const CrateSize = z.literal(CRATE_SIZES);
export type CrateSize = z.infer<typeof CrateSize>;
export const CrateType = z.enum(CRATE_TYPES);
export type CrateType = z.infer<typeof CrateType>;
export const BattleResource = z.enum(BATTLE_RES_IDS);

/**
 * How an item is counted. Crates are counted in crates and measured in SCU through
 * their size; materials in SCU; kits in kits; anything else in SCU or units, as
 * chosen on the item (plan 2B Assumption 1).
 */
export const ItemUnit = z.enum(['crates', 'scu', 'kits', 'units']);
export type ItemUnit = z.infer<typeof ItemUnit>;

/** The unit a category implies, or null when the item chooses SCU or units. */
export function unitOfCategory(category: SupCat): ItemUnit | null {
  if (category === 'Crates') return 'crates';
  if (category === 'Materials') return 'scu';
  if (category === 'Kits') return 'kits';
  return null;
}

export const Item = z.object({
  id: z.string(),
  name: z.string(),
  category: Category,
  unit: ItemUnit,
  /** Crates only. */
  crateType: CrateType.nullable(),
  battleResource: BattleResource.nullable(),
  notes: z.string(),
  active: z.boolean(),
  version: z.number().int(),
  created: z.iso.datetime(),
  updated: z.iso.datetime(),
});
export type Item = z.infer<typeof Item>;

const ItemFields = z.object({
  name: z.string().trim().min(1).max(80),
  category: Category,
  /** For categories other than crates, materials and kits: `scu` or `units` (default `units`). Ignored otherwise. */
  unit: z.enum(['scu', 'units']).default('units'),
  /** Crates only; ignored otherwise. */
  crateType: CrateType.nullable().default(null),
  battleResource: BattleResource.nullable().default(null),
  notes: z.string().max(1000).default(''),
});

/** Fixes the unit from the category, and drops a crate type a non-crate can't have. */
function settleItem<T extends z.output<typeof ItemFields>>(input: T): Omit<T, 'unit'> & { unit: ItemUnit } {
  return { ...input, unit: unitOfCategory(input.category) ?? input.unit, crateType: input.category === 'Crates' ? input.crateType : null };
}

export const ItemInput = ItemFields.transform(settleItem);
export const ItemUpdate = ItemFields.extend({ version: z.number().int().min(1) }).transform(settleItem);

/**
 * Where supply is: in the stockpile at a location (free text, matched ignoring case
 * and surrounding spaces), or aboard a ship.
 */
export const Place = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('stockpile'), location: z.string().trim().min(1).max(64) }),
  z.object({ kind: z.literal('ship'), shipId: RecordId }),
]);
export type Place = z.infer<typeof Place>;

/** A place as stored: a stockpile location also carries its key, the text trimmed and lower-cased. */
export const LotPlace = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('stockpile'), location: z.string(), locationKey: z.string() }),
  z.object({ kind: z.literal('ship'), shipId: z.string() }),
]);
export type LotPlace = z.infer<typeof LotPlace>;

/** A quality as typed: 0 to 1000, or null for unknown, which isn't 0. */
export const Quality = z.number().int().min(0).max(1000).nullable();

/** One stack of one item at one place (plan 2B Assumption 3). */
export const Lot = z.object({
  id: z.string(),
  item: z.string(),
  place: LotPlace,
  /** Crates: the crate's size in SCU. */
  crateSize: z.number().int().nullable(),
  /** Materials: null when unknown. */
  quality: z.number().int().nullable(),
  /** Materials. */
  refined: z.boolean(),
  /** Materials: a user id. */
  refinedBy: z.string().nullable(),
  /** A user who holds it for the org, or null. */
  holder: z.string().nullable(),
  /** Kits: the box's size in SCU. */
  boxSize: z.number().int().nullable(),
  /** Kits: what's packed in the box. */
  contents: z.string(),
  notes: z.string(),
  /** In the item's base unit: crates, kits or units counted, or thousandths of an SCU. */
  amount: z.number().int(),
  version: z.number().int(),
  created: z.iso.datetime(),
  updated: z.iso.datetime(),
});
export type Lot = z.infer<typeof Lot>;

/** A prep level: an item's target at a place, in the item's measure (crates in thousandths of an SCU). */
export const PrepLevel = z.object({
  id: z.string(),
  item: z.string(),
  place: LotPlace,
  target: z.number().int(),
  /** False for a level on an archived ship. */
  active: z.boolean(),
  version: z.number().int(),
});
export type PrepLevel = z.infer<typeof PrepLevel>;

/** What a movement or a change came from: a restock, a refinery job, a donation. */
export const Reference = z.object({ type: z.string().min(1).max(32), id: z.string().min(1).max(32) });
export type Reference = z.infer<typeof Reference>;

/** One change to a lot's amount, appended and never changed (plan 2B Approach). */
export const Movement = z.object({
  id: z.string(),
  lot: z.string(),
  item: z.string(),
  place: LotPlace,
  delta: z.number().int(),
  amountAfter: z.number().int(),
  reason: z.string(),
  ref: Reference.nullable(),
  /** The user who made the change, or null for another module's service. */
  actor: z.string().nullable(),
  created: z.iso.datetime(),
});
export type Movement = z.infer<typeof Movement>;

const Amount = z.number().int().min(0).max(MAX_AMOUNT);

/** One line of an add: what's added to the stack these details make at the place (R3). */
export const StockLine = z.object({
  crateSize: CrateSize.nullable().default(null),
  quality: Quality.default(null),
  refined: z.boolean().default(false),
  refinedBy: RecordId.nullable().default(null),
  holder: RecordId.nullable().default(null),
  boxSize: CrateSize.nullable().default(null),
  contents: z.string().max(1000).default(''),
  notes: z.string().max(1000).default(''),
  /** 0 skips the line. */
  amount: Amount,
});
export type StockLine = z.infer<typeof StockLine>;

/** Adds stock of one item at one place: the Add crates, Add materials and Add item sheets. */
export const AddStockInput = z.object({
  item: RecordId,
  place: Place,
  lines: z.array(StockLine).min(1).max(20),
  /** Sets the item's prep level at the place too, in its measure; 0 clears it. */
  prepTarget: Amount.nullable().default(null),
  reason: z.string().trim().max(200).default(''),
});

/** The stepper: a change to a lot's amount, summed over a burst of taps. */
export const AdjustInput = z.object({
  delta: z.number().int().min(-MAX_AMOUNT).max(MAX_AMOUNT).refine((delta) => delta !== 0, 'must not be 0'),
  reason: z.string().trim().max(200).default(''),
});

/** A lot's details. Its item, place and amount change only through stock changes and moves. */
export const LotUpdate = z.object({
  crateSize: CrateSize.nullable(),
  quality: Quality,
  refined: z.boolean(),
  refinedBy: RecordId.nullable(),
  holder: RecordId.nullable(),
  boxSize: CrateSize.nullable(),
  contents: z.string().max(1000),
  notes: z.string().max(1000),
  version: z.number().int().min(1),
});

export const PrepLevelInput = z.object({
  item: RecordId,
  place: Place,
  /** In the item's measure; 0 clears it. */
  target: Amount,
});

/** The stockpile and everything aboard ships: what the Supply page shows. */
export const StockView = z.object({
  items: z.array(Item),
  lots: z.array(Lot),
  prepLevels: z.array(PrepLevel),
  people: People,
  /** Lots whose holder has lost access. */
  holderLeft: z.array(z.string()),
});
export type StockView = z.infer<typeof StockView>;

export const ShipAvailability = z.enum(SHIP_AVAILABILITY);
export type ShipAvailability = z.infer<typeof ShipAvailability>;
export const ShipRole = z.enum(SHIP_ROLES);
export type ShipRole = z.infer<typeof ShipRole>;
export const ShipOp = z.enum(SHIP_OPS);
export type ShipOp = z.infer<typeof ShipOp>;

/** A ship's owner: a member, or a guest's name. */
export const ShipOwner = z.union([z.object({ userId: RecordId }).strict(), z.object({ name: z.string().trim().min(1).max(64) }).strict()]);
export type ShipOwner = z.infer<typeof ShipOwner>;

/** A support ship (plan 2B Assumption 7). */
export const Ship = z.object({
  id: z.string(),
  name: z.string(),
  hull: z.string(),
  owner: z.union([z.object({ userId: z.string() }), z.object({ name: z.string() })]).nullable(),
  /** True: Sierra tracks its cargo and prep levels. False: registry only. */
  stocked: z.boolean(),
  availability: ShipAvailability,
  claimReadyAt: z.iso.datetime().nullable(),
  /** The ship that carries it, one level deep. */
  carriedBy: z.string().nullable(),
  parkedAt: z.string(),
  /** Thousandths of an SCU, or null when not set. */
  cargoCapacity: z.number().int().nullable(),
  roles: z.array(ShipRole),
  supports: z.array(ShipOp),
  notes: z.string(),
  lastRestockedAt: z.iso.datetime().nullable(),
  active: z.boolean(),
  version: z.number().int(),
  created: z.iso.datetime(),
  updated: z.iso.datetime(),
});
export type Ship = z.infer<typeof Ship>;

const unique = <T>(values: T[]): T[] => [...new Set(values)];

export const ShipInput = z.object({
  name: z.string().trim().min(1).max(64),
  hull: z.string().trim().max(64).default(''),
  owner: ShipOwner.nullable().default(null),
  stocked: z.boolean().default(true),
  availability: ShipAvailability.default('Available'),
  claimReadyAt: z.iso.datetime().nullable().default(null),
  carriedBy: RecordId.nullable().default(null),
  parkedAt: z.string().trim().max(64).default(''),
  cargoCapacity: z.number().int().min(1).max(MAX_AMOUNT).nullable().default(null),
  roles: z.array(ShipRole).max(20).default([]).transform(unique),
  supports: z.array(ShipOp).max(20).default([]).transform(unique),
  notes: z.string().max(1000).default(''),
});
export const ShipUpdate = ShipInput.extend({ version: z.number().int().min(1) });

/** A ship's readiness (R8): worked out from its prep levels and what's aboard, never stored. */
export const ShipState = z.enum(['registry', 'down', 'noprep', 'short', 'ready']);
export type ShipState = z.infer<typeof ShipState>;

export const ShortLine = z.object({ item: z.string(), have: z.number().int(), target: z.number().int() });
export type ShortLine = z.infer<typeof ShortLine>;

export const ShipStatus = z.object({
  ship: z.string(),
  state: ShipState,
  /** How many prep levels it has (targets above 0). */
  prepLevels: z.number().int(),
  short: z.array(ShortLine),
  /** round((levels − short) ÷ levels × 100), or null with no prep levels or when registry only. */
  pct: z.number().int().nullable(),
  /** Thousandths of an SCU: crates, materials and SCU items aboard. */
  cargoUsed: z.number().int(),
});
export type ShipStatus = z.infer<typeof ShipStatus>;

/** Moves part or all of a lot to another place (R7). */
export const MoveInput = z.object({
  /** In the item's base unit; 0 is refused. */
  amount: z.number().int().min(0).max(MAX_AMOUNT),
  to: Place,
  reason: z.string().trim().max(200).default(''),
});

/** Archives a ship, moving what's aboard to a stockpile location (its "Parked at" by default). */
export const ArchiveInput = z.object({ location: z.string().trim().max(64).default(''), version: z.number().int().min(1) });

/** Restocks a ship's short prep levels, or one of them. */
export const RestockInput = z.object({
  item: RecordId.nullable().default(null),
  /** The preview's fingerprint, so the result can say if the stock changed in between. */
  fingerprint: z.string().max(4000).nullable().default(null),
});

/** One lot's part of a restock: from a stockpile lot to the ship. */
export const RestockMove = z.object({ from: z.string(), item: z.string(), amount: z.number().int(), crateSize: z.number().int().nullable(), fromLocation: z.string() });
export type RestockMove = z.infer<typeof RestockMove>;

/** A prep level still short after the restock, by how much (in its measure). */
export const RestockShort = z.object({ item: z.string(), need: z.number().int() });
export type RestockShort = z.infer<typeof RestockShort>;

export const RestockPlan = z.object({
  /** How many short prep levels it set out to fill: 0 means everything aboard is at its prep level. */
  levels: z.number().int(),
  moves: z.array(RestockMove),
  short: z.array(RestockShort),
});
export type RestockPlan = z.infer<typeof RestockPlan>;
